import * as React from "react";
import { Clock, MoonStar, Plus } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  StatusPill,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  formatClock,
  formatDays,
  sortSlots,
  useSeatSlots,
  useUpdateSeatSlot,
  type SeatSlot,
} from "@/lib/seatSlots/api";
import { SeatSlotEditorSheet } from "./SeatSlotEditorSheet";

/**
 * Phase SC-21d — the centre's shifts (seat slots).
 *
 * A compact card list rather than a `<DataTable>`: a centre runs a handful
 * of shifts, and each row needs its hours and days readable at a glance on
 * a phone. Learners only ever see active shifts that apply on the date
 * they pick (`GET /api/me/seat-slots`), in `display_order`.
 */
export function SeatSlotsCard({ canEdit }: { canEdit: boolean }) {
  const slotsQuery = useSeatSlots();
  const updateMutation = useUpdateSeatSlot();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SeatSlot | null>(null);

  const slots = React.useMemo(() => sortSlots(slotsQuery.data?.items ?? []), [slotsQuery.data]);
  const nextOrder = slots.reduce((max, s) => Math.max(max, s.display_order), 0) + 10;

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }

  function openEdit(slot: SeatSlot) {
    setEditing(slot);
    setEditorOpen(true);
  }

  function toggleActive(slot: SeatSlot) {
    updateMutation.mutate(
      { id: slot.id, body: { is_active: !slot.is_active } },
      {
        onSuccess: (saved) =>
          toast.success(
            saved.is_active ? `${saved.label} is bookable again.` : `${saved.label} deactivated.`,
          ),
        onError: (err) => toast.error(err.message || "Couldn't update the shift."),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="grid gap-1.5">
          <CardTitle className="text-base">Shifts</CardTitle>
          <CardDescription>
            The windows members can book a seat for. Learners pick from the active shifts that run
            on their chosen day.
          </CardDescription>
        </div>
        {canEdit ? (
          <Button size="sm" onClick={openCreate} className="shrink-0">
            <Plus aria-hidden="true" className="size-4" />
            Add shift
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {slotsQuery.isLoading ? (
          <div className="grid gap-2" aria-label="Loading shifts">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="bg-muted/60 h-16 w-full animate-pulse rounded-md motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : slotsQuery.isError ? (
          <div
            role="alert"
            className="border-destructive/30 bg-destructive/5 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2.5"
          >
            <span className="text-destructive text-sm">Couldn&apos;t load shifts.</span>
            <Button variant="outline" size="sm" onClick={() => void slotsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : slots.length === 0 ? (
          <div className="border-border/60 flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-8 text-center">
            <Clock aria-hidden="true" className="text-muted-foreground size-6" />
            <p className="text-sm font-medium">No shifts yet</p>
            <p className="text-muted-foreground max-w-sm text-xs">
              Members can&apos;t book a seat until at least one shift exists.
            </p>
            {canEdit ? (
              <Button size="sm" variant="outline" onClick={openCreate} className="mt-1">
                Add the first shift
              </Button>
            ) : null}
          </div>
        ) : (
          <ul className="divide-border/60 border-border/60 divide-y rounded-md border">
            {slots.map((slot) => (
              <li
                key={slot.id}
                className={cn(
                  "flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3",
                  !slot.is_active && "bg-muted/20",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "text-sm font-semibold",
                        !slot.is_active && "text-muted-foreground",
                      )}
                    >
                      {slot.label}
                    </span>
                    {slot.is_active ? null : <StatusPill status="inactive" />}
                    {slot.branch_id ? (
                      <span className="text-muted-foreground text-xs">Branch shift</span>
                    ) : null}
                  </div>
                  <p className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                    <span className="tabular-nums">
                      {formatClock(slot.start_time)} – {formatClock(slot.end_time)}
                    </span>
                    {slot.spans_midnight ? (
                      <MoonStar aria-label="Overnight" className="size-3.5" />
                    ) : null}
                    <span aria-hidden="true">·</span>
                    <span>{formatDays(slot.days_of_week)}</span>
                  </p>
                </div>
                {canEdit ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(slot)}
                      aria-label={`Edit ${slot.label}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={updateMutation.isPending}
                      onClick={() => toggleActive(slot)}
                      aria-label={`${slot.is_active ? "Deactivate" : "Reactivate"} ${slot.label}`}
                    >
                      {slot.is_active ? "Deactivate" : "Reactivate"}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <SeatSlotEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        existing={editing}
        nextDisplayOrder={nextOrder}
        existingKeys={slots.map((s) => s.key)}
      />
    </Card>
  );
}
